/**
 * Calculate estimated costs for upcoming refurbishment needs.
 * Uses configurable cost formulas per component.
 */
export function calculateRefurbishmentCosts(upcomingNeeds, units, costEstimates = []) {
  if (!upcomingNeeds?.needs?.length || !costEstimates?.length) return { items: [], totalCost: 0 };
  const items = [];
  for (const need of upcomingNeeds.needs) {
    const config = costEstimates.find(c => c.key === need.key && c.active !== false);
    if (!config) continue;
    let cost = 0;
    if (config.type === 'per_unit') {
      cost = (config.cost_per_unit || 0) * units;
    } else if (config.type === 'bracket') {
      const brackets = config.brackets || [];
      for (const b of brackets) {
        if (units <= b.max_units) { cost = b.cost; break; }
      }
    } else if (config.type === 'tiered') {
      const tiers = config.tiers || [];
      for (const t of tiers) {
        if (units <= t.max_units) {
          cost = t.base_cost + (t.per_extra_unit || 0) * Math.max(0, units - (t.extra_from || 0));
          break;
        }
      }
    }
    if (cost > 0) items.push({ key: need.key, label: need.label || config.label, cost, urgency: need.urgency });
  }
  return { items, totalCost: items.reduce((s, i) => s + i.cost, 0) };
}

export function calculateBuildingComplexity(data, settings = {}) {
  let score = 0;
  const units = data.units || 1;
  const buildings = data.number_of_buildings || 1;
  const upb = units / Math.max(buildings, 1);
  
  // Equipment weights - use settings if available, otherwise defaults
  const equipmentWeights = settings.equipment_weights || {};
  
  if (units <= 5) score += 15;
  else if (units <= 15) score += 25;
  else if (units <= 30) score += 35;
  else if (units <= 50) score += 45;
  else score += 55;
  
  // Configurable building complexity rules
  const buildingItems = settings.building_complexity_items || [];
  if (buildingItems.length > 0) {
    for (const item of buildingItems) {
      if (item.active === false) continue;
      if (item.type === 'per_building' && buildings > 1) {
        score += Math.min(buildings * (item.points || 0), item.max_points ?? 999);
      } else if (item.type === 'threshold_below' && upb < (item.threshold ?? 10)) {
        score += item.points || 0;
      } else if (item.type === 'threshold_above' && upb >= (item.threshold ?? 10)) {
        score += item.points || 0;
      }
    }
  } else {
    // Legacy fallback
    if (buildings > 1) score += Math.min(buildings * (equipmentWeights.building_points_per_unit ?? 3), equipmentWeights.building_points_max ?? 15);
    if (upb < (equipmentWeights.small_community_threshold ?? 10)) score += equipmentWeights.small_community_points ?? 10;
  }
  if (data.has_elevator) score += equipmentWeights.has_elevator ?? 12;
  if (data.has_underground_garage) score += equipmentWeights.has_underground_garage ?? 10;
  if (data.has_double_parkers) score += equipmentWeights.has_double_parkers ?? 18;
  if (data.has_sub_communities) score += equipmentWeights.has_sub_communities ?? 20;
  if (!data.has_central_heating) score += equipmentWeights.no_central_heating ?? -8;
  
  // Custom equipment items from settings
  const customEquipment = data.custom_equipment || {};
  const equipItems = settings.equipment_items || [];
  equipItems
    .filter(item => item.active !== false && item.key?.startsWith('custom_') && customEquipment[item.key])
    .forEach(item => { score += item.weight || 0; });
  
  return Math.max(0, Math.min(score, 100));
}

/**
 * Calculate fictive construction year when WEG conversion happened.
 * Conversion typically renews heating, electrical, windows (~60% weight)
 * but NOT roof, water pipes, facade (~40% weight).
 * Returns null if no conversion year or conversion not after construction.
 */
export function calculateFictiveYear(constructionYear, conversionYear) {
  if (!conversionYear || !constructionYear || conversionYear <= constructionYear) return null;
  // 60% renewal factor: heating, electrical, windows are renewed
  const renewalFactor = 0.6;
  const fictive = Math.round(constructionYear + (conversionYear - constructionYear) * renewalFactor);
  return fictive;
}

/**
 * Get the effective year to use for all calculations.
 * Priority: fictive_year_override > calculated fictive year > construction_year
 */
export function getEffectiveYear(data) {
  if (data.fictive_year_override) return data.fictive_year_override;
  const fictive = calculateFictiveYear(data.construction_year, data.conversion_year);
  if (fictive) return fictive;
  return data.construction_year || new Date().getFullYear();
}

export function calculateUpcomingRefurbishment(data, customThresholds = null, thresholdsList = null, excludePlannedRenovations = false) {
  const currentYear = new Date().getFullYear();
  const effectiveYear = getEffectiveYear(data);
  const renovations = data.renovations || {};
  const plannedRenovations = data.planned_renovations || {};
  const needs = [];
  
  // Use thresholds list if available (new CRUD format)
  if (thresholdsList && Array.isArray(thresholdsList) && thresholdsList.length > 0) {
    for (const item of thresholdsList) {
      if (item.active === false) continue;
      const renKey = item.renovation_key || item.key;
      const ageKey = item.age_key || (renKey + '_age');
      const label = item.label || renKey;
      const threshold = item.years || 30;
      const requiredEquipment = item.required_equipment;
      
      if (requiredEquipment && !data[requiredEquipment]) continue;
      if (renovations[renKey]) continue;
      
      // Skip if this item is in planned renovations and we want to exclude them
      if (excludePlannedRenovations && plannedRenovations[renKey]) continue;
      
      let componentAge = data[ageKey];
      if (componentAge == null) componentAge = currentYear - effectiveYear;
      if (componentAge >= threshold) {
        needs.push({
          key: renKey, label, age: componentAge, threshold,
          urgency: componentAge >= threshold * 1.2 ? "hoch" : "mittel"
        });
      }
    }
    return { needs, count: needs.length };
  }
  
  // Fallback: old dict format
  const defaultThresholds = {
    roof: 60, heating: 25, windows: 35, facade_paint: 20,
    electrical: 40, water_pipes: 50, elevator_modernization: 25,
  };
  
  const thresholds = customThresholds || defaultThresholds;
  
  const components = [
    ["roof", "roof_age", "Dachsanierung", null],
    ["heating", "heating_age", "Erneuerung Heizungsanlage", "has_central_heating"],
    ["windows", "windows_age", "Fenstererneuerung", null],
    ["facade_paint", "facade_age", "Fassadenanstrich/-sanierung", null],
    ["electrical", "electrical_age", "Erneuerung Elektroinstallation", null],
    ["water_pipes", "water_pipes_age", "Erneuerung Wasserleitungen", null],
    ["elevator_modernization", "elevator_age", "Aufzugsmodernisierung", "has_elevator"],
  ];
  
  for (const [renKey, ageKey, label, requiredEquipment] of components) {
    if (requiredEquipment && !data[requiredEquipment]) continue;
    const threshold = thresholds[renKey] ?? defaultThresholds[renKey] ?? 30;
    if (renovations[renKey]) continue;
    
    // Skip if this item is in planned renovations and we want to exclude them
    if (excludePlannedRenovations && plannedRenovations[renKey]) continue;
    
    let componentAge = data[ageKey];
    if (componentAge == null) componentAge = currentYear - effectiveYear;
    if (componentAge >= threshold) {
      needs.push({
        key: renKey, label, age: componentAge, threshold,
        urgency: componentAge >= threshold * 1.2 ? "hoch" : "mittel"
      });
    }
  }
  return { needs, count: needs.length };
}

export function calculateBuildingCondition(data, settings = {}) {
  const bcp = settings.building_condition_params || {};
  const currentYear = new Date().getFullYear();
  const effectiveYear = getEffectiveYear(data);
  const age = currentYear - effectiveYear;
  // Configurable age scoring
  const ageMin = bcp.age_min_score ?? 10;
  const ageMax = bcp.age_max_score ?? 85;
  const ageYears = bcp.age_max_years ?? 60;
  let baseScore;
  if (age <= 0) baseScore = ageMin;
  else if (age >= ageYears) baseScore = ageMax;
  else baseScore = ageMin + (age / ageYears) * (ageMax - ageMin);
  // Configurable defect penalty
  const defectMaxAge = bcp.defect_max_age ?? 6;
  const defectPenalty = bcp.defect_penalty ?? 25;
  if (age < defectMaxAge && data.has_construction_defects) baseScore += defectPenalty;
  // Configurable condition adjustments
  const gc = data.general_condition || 'gut';
  const gcAdj = {
    sehr_gut: bcp.condition_sehr_gut ?? -10,
    gut: bcp.condition_gut ?? 0,
    mittel: bcp.condition_mittel ?? 10,
    schlecht: bcp.condition_schlecht ?? 20,
  };
  baseScore += gcAdj[gc] || 0;
  // Configurable modernization reduction
  const renovations = data.renovations || {};
  const modWeights = data._modernization_weights || settings.modernization_weights || {};
  const defaultModReduction = bcp.default_modernization_reduction ?? 8;
  Object.entries(renovations).forEach(([k, v]) => {
    if (k !== 'other' && v === true) {
      baseScore -= (modWeights[k] ?? defaultModReduction);
    }
  });
  // Configurable planned renovation penalty
  const planned = data.planned_renovations || {};
  const plannedPenalty = bcp.planned_renovation_penalty ?? 10;
  if (typeof planned === 'object') {
    const hasPlanned = Object.entries(planned).some(([k, v]) => k !== 'other' && v === true) || !!planned.other;
    if (hasPlanned) baseScore += plannedPenalty;
  } else if (planned) {
    baseScore += plannedPenalty;
  }
  // Configurable upcoming need penalty (exclude items already in planned_renovations)
  const thresholdsList = data._thresholds_list || settings.refurbishment_thresholds_list || null;
  const upcoming = calculateUpcomingRefurbishment(data, null, thresholdsList);
  const plannedKeys = new Set(Object.entries(planned).filter(([k, v]) => k !== 'other' && v === true).map(([k]) => k));
  const filteredNeeds = upcoming.needs.filter(n => !plannedKeys.has(n.key));
  const upcomingPenalty = bcp.upcoming_need_penalty ?? 5;
  baseScore += filteredNeeds.length * upcomingPenalty;
  // Configurable repair weight (use per-item weights from settings if available)
  const repairs = data.major_repairs || {};
  const defaultRepairWeight = bcp.default_repair_weight ?? 3;
  const repairItemsConfig = data._repair_items_config || settings.repair_items || [];
  const repairWeightMap = {};
  if (repairItemsConfig.length > 0) {
    repairItemsConfig.forEach(r => { repairWeightMap[r.key] = r.weight ?? defaultRepairWeight; });
  }
  Object.entries(repairs).forEach(([k, v]) => {
    if (k !== 'other' && v === true) {
      baseScore += (repairWeightMap[k] ?? defaultRepairWeight);
    }
  });
  return Math.max(0, Math.min(baseScore, 100));
}

export function calculateHistoryScore(data, settings = {}) {
  const history = data.history || {};
  let score = 0;
  
  const historyItems = settings.history_items || data._history_items;
  let maxPossible = 0;
  
  if (historyItems && historyItems.length > 0) {
    for (const item of historyItems) {
      if (item.active === false) continue;
      const key = item.key;
      const type = item.type || 'freq';
      const value = history[key];
      
      if (type === 'freq') {
        if (value === 'keine' || value == null) score += item.weight_none || 0;
        else if (value === 'selten') score += item.weight_selten || 3.7;
        else if (value === 'haeufig') score += item.weight_haeufig || 8.4;
        maxPossible += item.weight_haeufig || 8.4;
      } else if (type === 'level') {
        if (value === 'niedrig') score += item.weight_niedrig || 0;
        else if (value === 'mittel' || value == null) score += item.weight_mittel || 3.7;
        else if (value === 'hoch') score += item.weight_hoch || 7;
        maxPossible += item.weight_hoch || 7;
      } else if (type === 'level_inv') {
        if (value === 'niedrig') score += item.weight_niedrig || 7;
        else if (value === 'mittel' || value == null) score += item.weight_mittel || 3.7;
        else if (value === 'hoch') score += item.weight_hoch || 0;
        maxPossible += item.weight_niedrig || 7;
      } else if (type === 'sepa') {
        if (value === 'alle' || value == null) score += item.weight_alle || 0;
        else if (value === 'meiste') score += item.weight_meiste || 3.7;
        else if (value === 'wenige') score += item.weight_wenige || 8.4;
        maxPossible += item.weight_wenige || 8.4;
      } else if (type === 'bool_inv') {
        if (value === true) score += item.weight_true || 0;
        else score += item.weight_false || 5.6;
        maxPossible += item.weight_false || 5.6;
      }
    }
    maxPossible += 15; // max penalty for reserves
  } else {
    // Fallback to hardcoded scoring
    const freq = { keine: 0, selten: 8, haeufig: 18 };
    const levelInv = { niedrig: 15, mittel: 8, hoch: 0 };
    const levelMap = { niedrig: 0, mittel: 8, hoch: 15 };
    const sepaMap = { alle: 0, meiste: 8, wenige: 18 };
    score += freq[history.extraordinary_meetings] || 0;
    score += freq[history.ownership_changes] || 0;
    score += freq[history.payment_defaults] || 0;
    score += freq[history.disturbances] || 0;
    score += freq[history.tenant_changes] || 0;
    score += freq[history.special_assessments] || 0;
    score += levelInv[history.reserves_level] || 8;
    score += freq[history.court_proceedings] || 0;
    score += levelMap[history.owner_information_needs] || 8;
    score += freq[history.insurance_claims] || 0;
    score += freq[history.circular_resolutions] || 0;
    score += sepaMap[history.sepa_participation] || 0;
    if (!history.has_professional_caretaker) score += 12;
    maxPossible = 18 * 8 + 15 * 2 + 18 + 12 + 15;
  }
  
  if (data.has_advisory_board) score -= 5;
  if (data.has_sufficient_reserves === true) score -= 10;
  else if (data.has_sufficient_reserves === false) score += 15;
  
  if (maxPossible <= 0) maxPossible = 100;
  return Math.max(0, Math.min((score / maxPossible) * 100, 100));
}

export function calculateDistancePenalty(data) {
  const distance = data.office_distance_km;
  if (distance && distance > 20) return Math.min((distance - 20) * 0.5, 15);
  return 0;
}

export function calculateScores(data, settings = {}) {
  const complexity = calculateBuildingComplexity(data, settings);
  const condition = calculateBuildingCondition(data, settings);
  const history = calculateHistoryScore(data, settings);
  const distancePenalty = calculateDistancePenalty(data);
  const sw = settings.score_weights || { complexity: 30, condition: 35, history: 35 };
  const total = Math.max(0, Math.min(complexity * (sw.complexity / 100) + condition * (sw.condition / 100) + history * (sw.history / 100) + distancePenalty, 100));
  
  // Use configurable rating labels if available
  const ratingLabels = settings.rating_labels || [];
  let demandPct = 100;
  let category = 'durchschnittlich';
  let ratingLabelKey = null;
  let ratingColor = 'slate';
  
  if (ratingLabels.length > 0) {
    // Sort by min_score descending - match the first label where total >= min_score
    const sortedLabels = [...ratingLabels].filter(l => l.active !== false).sort((a, b) => (b.min_score || 0) - (a.min_score || 0));
    for (const label of sortedLabels) {
      if (total >= (label.min_score || 0)) {
        demandPct = label.demand_pct || 100;
        category = label.label || 'Durchschnittlich';
        ratingLabelKey = label.key || '';
        ratingColor = label.color || 'slate';
        break;
      }
    }
  } else {
    // Fallback to hardcoded values
    if (total <= 20) { demandPct = 50; category = 'unterdurchschnittlich'; }
    else if (total <= 35) { demandPct = 75; category = 'leicht unterdurchschnittlich'; }
    else if (total <= 55) { demandPct = 100; category = 'durchschnittlich'; }
    else if (total <= 75) { demandPct = 150; category = 'überdurchschnittlich'; }
    else { demandPct = 150; category = 'stark überdurchschnittlich'; }
  }
  
  return {
    buildingComplexity: Math.round(complexity * 10) / 10,
    buildingCondition: Math.round(condition * 10) / 10,
    historyScore: Math.round(history * 10) / 10,
    distancePenalty: Math.round(distancePenalty * 10) / 10,
    totalScore: Math.round(total * 10) / 10,
    demandPercentage: demandPct,
    demandCategory: category,
    ratingLabelKey,
    ratingColor,
  };
}

export function getBaseFeeForUnits(units) {
  const bp = [[100,20],[50,28],[30,30],[20,35],[15,40],[10,50],[6,65],[1,65]];
  if (units >= 100) return 20;
  for (let i = 0; i < bp.length - 1; i++) {
    const [uu, uf] = bp[i], [lu, lf] = bp[i+1];
    if (units >= lu) {
      const r = (units - lu) / Math.max(uu - lu, 1);
      return Math.round((lf + (uf - lf) * r) * 100) / 100;
    }
  }
  return 65;
}

export function getConfiguredBaseFee(units, settings = {}) {
  // If base_fee_per_unit is explicitly active and set, use it (overrides tiers)
  if (settings.base_fee_per_unit_active && settings.base_fee_per_unit && settings.base_fee_per_unit > 0) return settings.base_fee_per_unit;
  
  // Check for new fee_tiers_config format
  const feeTiersConfig = settings.fee_tiers_config;
  if (feeTiersConfig && feeTiersConfig.length > 0) {
    // Sort by min value to ensure correct order
    const sortedTiers = [...feeTiersConfig].sort((a, b) => (a.min || 0) - (b.min || 0));
    for (const tier of sortedTiers) {
      const tierMin = tier.min || 0;
      const tierMax = tier.max || 999;
      if (units >= tierMin && units <= tierMax) {
        return tier.rate || 30;
      }
    }
    // If units exceed all tiers, use the last tier's rate
    if (sortedTiers.length > 0) {
      return sortedTiers[sortedTiers.length - 1].rate || 25;
    }
  }
  
  // Fallback to old fee_tiers format or defaults
  const feeTiers = settings.fee_tiers || {};
  const defaults = { 
    kleinstgemeinschaft_mini: 55,  // 2-3 units
    kleinstgemeinschaft: 50,       // 4-6 units
    klein: 40,                     // 7-15 units
    mittel: 35,                    // 16-30 units
    gross: 30,                     // 31-60 units
    sehr_gross: 27,                // 61-100 units
    grossanlage: 25                // 101+ units
  };
  const tiers = { ...defaults, ...feeTiers };
  if (units <= 3) return tiers.kleinstgemeinschaft_mini;
  if (units <= 6) return tiers.kleinstgemeinschaft;
  if (units <= 15) return tiers.klein;
  if (units <= 30) return tiers.mittel;
  if (units <= 60) return tiers.gross;
  if (units <= 100) return tiers.sehr_gross;
  return tiers.grossanlage;
}

export function generateRecommendation(data, settings = {}) {
  const units = data.units || 1;
  const buildings = data.number_of_buildings || 1;
  const upb = units / Math.max(buildings, 1);
  const baseFee = getConfiguredBaseFee(units, settings);
  const scores = calculateScores(data, settings);
  const demandPct = scores.demandPercentage;
  const factors = [];
  let recMin = baseFee;
  let recMax = baseFee;
  let assessment = 'standard';

  // --- Complexity factors ---
  if (scores.buildingComplexity >= 60)
    factors.push({ label: 'Hohe Gebäudekomplexität', detail: `Score ${scores.buildingComplexity.toFixed(0)}/100`, effect: '+', category: 'complexity' });
  else if (scores.buildingComplexity <= 25)
    factors.push({ label: 'Geringe Gebäudekomplexität', detail: `Score ${scores.buildingComplexity.toFixed(0)}/100`, effect: '-', category: 'complexity' });

  if (data.has_elevator) factors.push({ label: 'Personenaufzug vorhanden', effect: '+', category: 'complexity' });
  if (data.has_underground_garage) factors.push({ label: 'Tiefgarage vorhanden', effect: '+', category: 'complexity' });
  if (data.has_double_parkers) factors.push({ label: 'Doppelparker/Hebebühnen', effect: '+', category: 'complexity' });
  // Custom equipment items
  const customEquipmentFactors = data.custom_equipment || {};
  const eqItems = settings.equipment_items || [];
  eqItems
    .filter(item => item.active !== false && item.key?.startsWith('custom_') && customEquipmentFactors[item.key])
    .forEach(item => {
      factors.push({ label: item.label, effect: item.weight >= 0 ? '+' : '-', category: 'complexity' });
    });

  // --- Condition factors ---
  if (scores.buildingCondition >= 60)
    factors.push({ label: 'Hoher Gebäudezustandsbedarf', detail: `Score ${scores.buildingCondition.toFixed(0)}/100`, effect: '+', category: 'condition' });
  else if (scores.buildingCondition <= 25)
    factors.push({ label: 'Guter Gebäudezustand', detail: `Score ${scores.buildingCondition.toFixed(0)}/100`, effect: '-', category: 'condition' });

  const currentYear = new Date().getFullYear();
  const effectiveYear = getEffectiveYear(data);
  const buildingAge = currentYear - effectiveYear;
  if (buildingAge >= 40)
    factors.push({ label: `Gebäudealter ${buildingAge} Jahre`, effect: '+', category: 'condition' });

  // --- History factors ---
  if (scores.historyScore >= 40)
    factors.push({ label: 'Aufwändige Verwaltungshistorie', detail: `Score ${scores.historyScore.toFixed(0)}/100`, effect: '+', category: 'history' });

  // --- Size/community factors (no longer sets assessment) ---
  if (upb < 10) {
    factors.push({ label: 'Kleinstgemeinschaft', detail: `${upb.toFixed(0)} Einh./Gebäude`, effect: '+', category: 'size' });
  } else if (upb > 18) {
    factors.push({ label: 'Skaleneffekte', detail: `${upb.toFixed(0)} Einh./Gebäude`, effect: '-', category: 'size' });
  }

  // --- Demand adjustments (determines assessment) ---
  // Use configurable recommendation_tiers from settings (same as backend)
  const recTiers = settings.recommendation_tiers || [];
  if (recTiers.length > 0) {
    const sortedTiers = [...recTiers].sort((a, b) => (b.min_demand || 0) - (a.min_demand || 0));
    for (const tier of sortedTiers) {
      if (demandPct >= (tier.min_demand || 0)) {
        recMin = Math.round(baseFee * (tier.min_multiplier || 1.0) * 100) / 100;
        recMax = Math.round(baseFee * (tier.max_multiplier || 1.0) * 100) / 100;
        assessment = tier.assessment || 'standard';
        break;
      }
    }
  } else {
    // Fallback to hardcoded logic
    if (demandPct > 150) {
      recMin = Math.round(recMin * 1.3 * 100) / 100;
      recMax = Math.round(recMax * 1.5 * 100) / 100;
      assessment = 'besonderer_bedarf';
    } else if (demandPct > 110) {
      recMin = Math.round(recMin * 1.1 * 100) / 100;
      recMax = Math.round(recMax * 1.25 * 100) / 100;
      assessment = 'erhoeht';
    } else if (demandPct <= 75) {
      recMin = Math.round(recMin * 0.9 * 100) / 100;
      assessment = 'reduziert';
    }
  }
  // Demand factor labels
  if (demandPct > 150) {
    factors.push({ label: 'Stark überdurchschnittlicher Bedarf', detail: `${demandPct}%`, effect: '++', category: 'demand' });
  } else if (demandPct > 110) {
    factors.push({ label: 'Überdurchschnittlicher Bedarf', detail: `${demandPct}%`, effect: '+', category: 'demand' });
  } else if (demandPct <= 75) {
    factors.push({ label: 'Unterdurchschnittlicher Bedarf', detail: `${demandPct}%`, effect: '-', category: 'demand' });
  }

  // --- Distance surcharge (same as backend) ---
  const OFFICE_LAT = 50.0782;
  const OFFICE_LON = 8.2398;
  let distance = data.office_distance_km;
  if (!distance && data.latitude && data.longitude) {
    const R = 6371;
    const dLat = (data.latitude - OFFICE_LAT) * Math.PI / 180;
    const dLon = (data.longitude - OFFICE_LON) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(OFFICE_LAT*Math.PI/180)*Math.cos(data.latitude*Math.PI/180)*Math.sin(dLon/2)**2;
    distance = R * 2 * Math.asin(Math.sqrt(a));
  }
  if (distance && distance > 20) {
    recMin = Math.round(recMin * 1.05 * 100) / 100;
    recMax = Math.round(recMax * 1.10 * 100) / 100;
    factors.push({ label: 'Entfernungszuschlag', detail: `${Math.round(distance)} km`, effect: '+', category: 'distance' });
  }

  if (data.has_construction_defects) {
    recMin = Math.round(recMin * 1.10 * 100) / 100;
    recMax = Math.round(recMax * 1.15 * 100) / 100;
    factors.push({ label: 'Baumängel vorhanden', effect: '+', category: 'condition' });
  }
  if (data.has_sub_communities) {
    recMin = Math.round(recMin * 1.15 * 100) / 100;
    recMax = Math.round(recMax * 1.2 * 100) / 100;
    factors.push({ label: 'Untergemeinschaften', effect: '++', category: 'complexity' });
  }
  const gc = data.general_condition || 'gut';
  if (gc === 'schlecht') factors.push({ label: 'Schlechter Allgemeinzustand', effect: '+', category: 'condition' });
  else if (gc === 'sehr_gut') factors.push({ label: 'Sehr guter Allgemeinzustand', effect: '-', category: 'condition' });

  const recommendedFeeRaw = Math.floor((recMin + recMax) / 2);

  // === Mindesthonorar-Logik ===
  const minimumFee = settings.minimum_fee ?? 300;
  const minimumFeeReductionPercent = settings.minimum_fee_reduction_percent ?? 10;
  let recommendedFee = recommendedFeeRaw;
  let minimumFeeApplied = false;
  let minimumFeeReason = null;
  let minimumFeeEffective = minimumFee;
  const monthlyTotal = recommendedFeeRaw * units;
  
  if (minimumFee > 0 && monthlyTotal < minimumFee) {
    let effectiveMinimum = minimumFee;
    if (assessment === 'reduziert') {
      effectiveMinimum = Math.floor(minimumFee * (1 - minimumFeeReductionPercent / 100));
      minimumFeeReason = `Günstige Konditionen (reduziert um ${minimumFeeReductionPercent}%)`;
      minimumFeeEffective = effectiveMinimum;
    } else if (assessment === 'erhoeht') {
      minimumFeeReason = 'Erhöhter Aufwand';
    } else if (assessment === 'besonderer_bedarf') {
      minimumFeeReason = 'Besonderer Bedarf';
    } else {
      minimumFeeReason = 'Standardvertrag';
    }
    if (monthlyTotal < effectiveMinimum) {
      recommendedFee = Math.ceil(effectiveMinimum / units);
      minimumFeeApplied = true;
    }
  }

  return { factors, recMin, recMax, assessment, baseFee, recommendedFee, scores, minimumFeeApplied, minimumFeeValue: minimumFee, minimumFeeEffective, minimumFeeReason };
}

export function estimateFees(data, settings = {}) {
  const units = data.units || 1;
  const baseFeePerUnit = data.custom_base_fee || getConfiguredBaseFee(units, settings);
  const annualBaseFee = baseFeePerUnit * units * 12;
  const totalEstimatedAnnual = annualBaseFee;
  return {
    baseFeePerUnit: Math.round(baseFeePerUnit * 100) / 100,
    annualBaseFee: Math.round(annualBaseFee * 100) / 100,
    totalEstimatedAnnual: Math.round(totalEstimatedAnnual * 100) / 100,
  };
}

export const defaultAnalysisData = {
  name: '', units: 10,
  has_elevator: false, has_underground_garage: false,
  has_double_parkers: false,
  has_sub_communities: false, has_advisory_board: false,
  has_central_heating: true, is_portfolio_property: false,
  construction_year: 1990, conversion_year: null,
  fictive_year: null, fictive_year_override: null,
  renovations: {
    heating: false, water_pipes: false, facade_paint: false,
    insulation: false, basement_sealing: false, windows: false,
    roof: false, elevator_modernization: false, electrical: false,
    balconies: false, other: '',
  },
  planned_renovations: {
    heating: false, water_pipes: false, facade_paint: false,
    insulation: false, basement_sealing: false, windows: false,
    roof: false, elevator_modernization: false, electrical: false,
    balconies: false, other: '',
  },
  planned_renovations_notes: '',
  planned_renovations_cost: 0,
  major_repairs: {
    heating_repair: false,
    elevator_repair: false,
    roof_repair: false,
    stairwell_repair: false,
    double_parker_repair: false,
    windows_repair: false,
    facade_repair: false,
    outdoor_repair: false,
    electrical_repair: false,
    water_pipes_repair: false,
    other: '',
  },
  history: {
    extraordinary_meetings: 'keine', ownership_changes: 'keine',
    payment_defaults: 'keine', disturbances: 'keine',
    tenant_changes: 'keine', special_assessments: 'keine',
    reserves_level: 'mittel', court_proceedings: 'keine',
    owner_information_needs: 'mittel', insurance_claims: 'keine',
    has_professional_caretaker: false, sepa_participation: 'alle',
    circular_resolutions: 'keine',
  },
  contact_name: '', contact_email: '', contact_phone: '',
  address: '', city: '', zip_code: '',
  latitude: null, longitude: null, custom_base_fee: null,
  number_of_buildings: 1, has_construction_defects: false,
  general_condition: 'gut', office_distance_km: null,
  has_sufficient_reserves: null, // null = not answered, true = yes, false = no
  custom_equipment: {}, // dynamic custom equipment items from admin settings
  // Component ages
  heating_age: null, roof_age: null, windows_age: null,
  facade_age: null, electrical_age: null, water_pipes_age: null,
  elevator_age: null, water_filter_age: null,
};
